/**
 * 登录状态管理模块
 * 管理用户 token、用户信息、权限、角色、动态菜单等状态
 */
import axios from 'axios';
import setting from '@/config/setting';
import util from '@/utils/util';

/**
 * 从 sessionStorage 恢复持久化的用户信息
 * 使用 try-catch 防止 JSON 解析异常导致页面白屏
 */
let loginUser = {};
try {
  loginUser = JSON.parse(sessionStorage.getItem(setting.userStoreName) || '{}') || {};
} catch (e) {
  console.error(e);
}

export default {
  namespaced: true,
  state: {
    /** 当前用户的登录 token */
    token: sessionStorage.getItem(setting.tokenStoreName),
    /** 当前用户基本信息对象 */
    user: loginUser,
    /** 当前用户权限列表 */
    authorities: [],
    /** 当前用户角色列表 */
    roles: [],
    /** 当前用户的菜单数据（null 表示未加载，加载后为数组） */
    menus: null,
  },
  mutations: {
    /**
     * 通用状态设置 mutation
     * @param {Object} state - Vuex state
     * @param {Object} obj - { key: 状态字段名, value: 状态值 }
     */
    SET: (state, obj) => {
      state[obj.key] = obj.value;
    },
  },
  actions: {
    /**
     * 缓存 token 到 sessionStorage 和 Vuex
     * token 为空时同时清除菜单缓存
     * @param {string} [token] - 新的 token 值，不传则清除 token
     */
    setToken({ commit }, token) {
      sessionStorage.removeItem(setting.tokenStoreName);
      if (token) sessionStorage.setItem(setting.tokenStoreName, token);
      else commit('SET', { key: 'menus', value: null });
      commit('SET', { key: 'token', value: token });
      // 同步更新 axios 全局请求头
      axios.defaults.headers.common[setting.tokenHeaderName] = token;
    },

    /**
     * 缓存用户信息到 sessionStorage 和 Vuex
     * @param {Object} [user] - 用户信息对象，不传则清除用户信息
     */
    setUser({ commit }, user) {
      sessionStorage.removeItem(setting.userStoreName);
      if (user) sessionStorage.setItem(setting.userStoreName, JSON.stringify(user));
      commit('SET', { key: 'user', value: user });
    },

    /**
     * 设置用户权限列表
     * @param {Array} authorities - 权限标识数组
     */
    setAuthorities({ commit }, authorities) {
      commit('SET', { key: 'authorities', value: authorities });
    },

    /**
     * 设置用户菜单数据
     * @param {Array} menus - 菜单树形数据
     */
    setMenus({ commit }, menus) {
      commit('SET', { key: 'menus', value: menus });
    },

    /**
     * 获取用户菜单并转换为路由配置
     * 优先从缓存读取，缓存为空时请求接口获取
     * @returns {Promise<Object>} 路由配置对象 { path, name, children, redirect }
     */
    getMenuRouters({ commit, state }) {
      return new Promise((resolve, reject) => {
        // 已有缓存则直接返回
        if (state.menus) return resolve(menusToRoute(state.menus));
        // 未配置菜单接口则返回空
        if (!setting.menuUrl) {
          commit('SET', { key: 'menus', value: [] });
          return resolve(null);
        }
        axios
          .get(setting.menuUrl)
          .then((res) => {
            let menus = setting.parseMenu ? setting.parseMenu(res.data) : res.data.data;
            resolve(menusToRoute(menus));
            commit('SET', { key: 'menus', value: menus });
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
  },
};

/**
 * 菜单数据转换为 Vue Router 路由配置
 * 遍历菜单树，提取有效路径生成路由对象
 * @param {Array} menus - 后端返回的菜单树形数据
 * @returns {Object} 路由配置对象 { path, name, children, redirect }
 */
function menusToRoute(menus) {
  let route = { path: '/', name: 'main', children: [], redirect: null };
  util.eachTreeData(menus, (item) => {
    // 执行自定义菜单项解析
    if (setting.parseMenuItem) item = setting.parseMenuItem(item);
    // 合并 meta 信息
    item.meta = Object.assign({ title: item.title, icon: item.icon, hide: item.hide, uid: item.uid }, item.meta);
    // 过滤外部链接，只处理内部路由
    if (item.path && !item.path.startsWith('http://') && !item.path.startsWith('https://') && !item.path.startsWith('//')) {
      // 第一个有效菜单项作为默认重定向地址
      if (!route.redirect) {
        route.redirect = item.path;
        if (!setting.homeTitle) setting.homeTitle = item.meta.title;
      }
      route.children.push({
        path: item.path,
        name: item.name || item.path,
        meta: item.meta,
        component: () => import('@/views' + (item.component || item.path)),
      });
    }
  });
  return route;
}
